import { useCallback, useMemo } from "react";
import { useSnackbar } from "notistack";
import type { Step } from "ui";

import { useGetLayersQuery, useGetVersionQuery, useUpdateVersionArchitectureMutation } from "fond/api";
import { DEFAULT_DATA_PREP_CONFIGURATION } from "fond/architecture/dataPrepConfiguration";
import { useCustomWorkflow, WorkflowState } from "fond/hooks/useCustomWorkflow";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { loadProject } from "fond/project/redux";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";

import { BaseDataGating, getBaseDataGating, isWorkflowRunning } from "./gating";
import {
  BaseDataMissingLayers,
  BaseDataPresence,
  GENERATION_KEYS,
  GENERATION_WORKFLOW_KEYS,
  GenerationKey,
  getBaseDataAnyPresence,
  getBaseDataMissingLayers,
  getBaseDataPresence,
} from "./layers";
import { getGenerationProgressSteps, getLatestFailure } from "./progress";
import { getCompletedEndTime, getStaleness, Staleness } from "./staleness";
import { BaseDataTasks, getBusyBaseDataKeys } from "./tasks";
import { useOwnedBaseDataTasks } from "./useBaseDataTasks";

type UseBaseDataGenerationProps = {
  projectId: string | undefined;
  versionId: string;
  readOnly: boolean;
};

export type BaseDataGeneration = {
  present: BaseDataPresence;
  /** Whether each base data layer has data in any of its planner layers. */
  hasData: BaseDataPresence;
  missing: BaseDataMissingLayers;
  staleness: Staleness;
  gating: BaseDataGating;
  workflowStates: Record<GenerationKey, WorkflowState>;
  /** Generation workflows currently starting or in progress. */
  running: GenerationKey[];
  /** Whether any generation workflow is running. */
  isRunning: boolean;
  /** The most recently run generation workflow, if its execution failed. */
  failed: GenerationKey[];
  /** Progress of the running generation workflows, and of the failed one when it isn't running again. */
  progressSteps: Step[];
  /** The task of each base data row whose flyout started one (see `getOwnedBaseDataTasks`). */
  ownedTasks: BaseDataTasks;
  /** Run a generation workflow. */
  run(key: GenerationKey): void;
  cancel(): void;
};

/** Return the state of base data generation for a version, and controls to run and cancel it. */
export const useBaseDataGeneration = ({ projectId, versionId, readOnly }: UseBaseDataGenerationProps): BaseDataGeneration => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { data: layers } = useGetLayersQuery(versionId, { skip: !versionId });
  const { layerKeys } = useGetPlannerLayers(versionId);
  const [updateVersionArchitecture, { isLoading: isSavingArchitecture }] = useUpdateVersionArchitectureMutation();

  const handleOnComplete = useCallback(() => {
    if (projectId) dispatch(loadProject({ uuid: projectId, forceRefetch: true }));
  }, [dispatch, projectId]);

  const ug = useCustomWorkflow({ versionId, key: GENERATION_WORKFLOW_KEYS.ug, onComplete: handleOnComplete });
  const leadins = useCustomWorkflow({ versionId, key: GENERATION_WORKFLOW_KEYS.leadins, onComplete: handleOnComplete });
  const legacy = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP });
  const runWorkflows = useMemo(() => ({ ug: ug.runWorkflow, leadins: leadins.runWorkflow }), [ug.runWorkflow, leadins.runWorkflow]);
  const cancelWorkflows = useMemo(() => ({ ug: ug.cancelWorkflow, leadins: leadins.cancelWorkflow }), [ug.cancelWorkflow, leadins.cancelWorkflow]);

  const present = useMemo(() => getBaseDataPresence(layerKeys, Boolean(version?.UploadedArea)), [layerKeys, version?.UploadedArea]);
  const missing = useMemo(() => getBaseDataMissingLayers(layerKeys), [layerKeys]);
  const hasData = useMemo(() => getBaseDataAnyPresence(missing, Boolean(version?.UploadedArea)), [missing, version?.UploadedArea]);

  const ugEndTime = getCompletedEndTime(ug.workflowState, ug.endTime);
  const leadinsEndTime = getCompletedEndTime(leadins.workflowState, leadins.endTime);
  const legacyEndTime = getCompletedEndTime(legacy.workflowState, legacy.endTime);
  const layerLastModified = useMemo(
    () => Object.fromEntries(Object.values(layers?.entities ?? {}).map((layer) => [layer.LayerKey, layer.UpdateTime])),
    [layers]
  );
  const staleness = useMemo(
    () =>
      getStaleness({
        layerLastModified,
        ugGenerationEndTime: ugEndTime,
        leadinGenerationEndTime: leadinsEndTime,
        legacyDataPrepEndTime: legacyEndTime,
        architectureLastModifiedAt: version?.Architecture?.LastModifiedAt,
        hasData: { ug: hasData.ug, leadins: hasData.leadins },
      }),
    [layerLastModified, ugEndTime, leadinsEndTime, legacyEndTime, version?.Architecture?.LastModifiedAt, hasData.ug, hasData.leadins]
  );

  const workflowStates: Record<GenerationKey, WorkflowState> = useMemo(
    () => ({ ug: ug.workflowState, leadins: leadins.workflowState }),
    [ug.workflowState, leadins.workflowState]
  );
  const running = useMemo(() => GENERATION_KEYS.filter((key) => isWorkflowRunning(workflowStates[key])), [workflowStates]);
  const latestFailure = getLatestFailure({
    ug: { state: ug.workflowState, endTime: ug.endTime },
    leadins: { state: leadins.workflowState, endTime: leadins.endTime },
  });
  const failed = useMemo(() => (latestFailure ? [latestFailure] : []), [latestFailure]);
  const isRunning = running.length > 0;
  const ownedTasks = useOwnedBaseDataTasks();
  const busy = useMemo(() => getBusyBaseDataKeys(ownedTasks), [ownedTasks]);

  const gating = useMemo(
    () =>
      getBaseDataGating({
        present,
        missing,
        staleness,
        running,
        starting: isSavingArchitecture,
        busy,
        hasArchitecture: Boolean(version?.Architecture),
        readOnly,
      }),
    [present, missing, staleness, running, isSavingArchitecture, busy, version?.Architecture, readOnly]
  );

  const progressSteps = useMemo(
    () =>
      getGenerationProgressSteps({
        keys: [...running, ...failed.filter((key) => !running.includes(key))],
        stages: { ug: ug.stages, leadins: leadins.stages },
      }),
    [running, failed, ug.stages, leadins.stages]
  );

  const run = useCallback(
    async (key: GenerationKey) => {
      const runWorkflow = runWorkflows[key];
      if (!runWorkflow) return;

      // Architectures saved outside planner_extended may lack a data prep configuration, so ensure we set one.
      if (version?.Architecture && !version.Architecture.DataPrepConfiguration) {
        try {
          await updateVersionArchitecture({ versionId, architecture: { DataPrepConfiguration: DEFAULT_DATA_PREP_CONFIGURATION } }).unwrap();
        } catch {
          enqueueSnackbar("Failed to save Data Prep Settings.", { variant: "error" });
          return;
        }
      }

      await runWorkflow();
    },
    [runWorkflows, version?.Architecture, updateVersionArchitecture, versionId, enqueueSnackbar]
  );

  const cancel = useCallback(() => {
    running.forEach((key) => cancelWorkflows[key]?.());
  }, [running, cancelWorkflows]);

  return useMemo(
    () => ({
      present,
      hasData,
      missing,
      staleness,
      gating,
      workflowStates,
      running,
      isRunning,
      failed,
      progressSteps,
      ownedTasks,
      run,
      cancel,
    }),
    [present, hasData, missing, staleness, gating, workflowStates, running, isRunning, failed, progressSteps, ownedTasks, run, cancel]
  );
};
